/* eslint-disable react/jsx-props-no-spreading */
import { useFormik } from 'formik';
import { IMaskInput } from 'react-imask';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'es-toolkit';
// mantine
import {
    Avatar,
    Button,
    FileButton,
    Grid,
    Group,
    Input,
    MultiSelect,
    Select,
    Textarea,
    TextInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
// icons
import { Building2, User } from 'lucide-react';

// components
import { CopyButton } from '@newo/ui-kit';
// utils
import type { TProfile } from '@newo/api/gateway';
import { base64ToImageSource } from '@newo/toolkit/image';
import { processFormData } from '@newo/toolkit/bff';
import validationSchema from '@/pages/Profile/components/AccountForm/AccountForm.schema.ts';
import {
    QUERY_KEYS,
    useUpdateProfileMutation
} from '@/services/customer/profile/profile.queries.ts';
import { INDUSTRIES } from '@/pages/Profile/Profile.const.ts';

interface AccountFormProps {
    profile: TProfile;
}

const MAX_FILE_SIZE = 500_000; // 500Kb
const MAX_WIDTH = 500; // 500px
const MAX_HEIGHT = 500; // 500px

const AccountForm = ({ profile }: AccountFormProps) => {
    const queryClient = useQueryClient();
    const { mutate: update, isPending } = useUpdateProfileMutation();

    const initialValues = {
        organization_name: profile.organization_name ?? '',
        website: profile.website ?? '',
        self_description: profile.self_description ?? '',
        industry: profile.industry ?? [],
        number_of_employees: profile.number_of_employees ?? '',
        country: profile.country ?? '',
        address: {
            address_line_1: profile.address?.address_line_1 ?? '',
            address_line_2: profile.address?.address_line_2 ?? '',
            city: profile.address?.city ?? '',
            state: profile.address?.state ?? '',
            zipcode: profile.address?.zipcode ?? ''
        },
        logo: profile.logo ?? '',
        first_name: profile.first_name ?? '',
        last_name: profile.last_name ?? '',
        job_title: profile.job_title ?? '',
        email: profile.email,
        phone_number: profile.phone_number ?? ''
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(formData, true);
            update(
                {
                    data: {
                        ...profile,
                        ...data,
                        address: {
                            ...profile.address,
                            ...data.address
                        }
                    }
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: QUERY_KEYS.getProfile()
                        });
                        formik.resetForm({ values: formData });
                        notifications.show({
                            color: 'green',
                            title: 'Success!',
                            message: 'Profile saved'
                        });
                    },
                    onSettled: () => {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleImageDrop = (file: File | null) => {
        if (file) {
            if (file.size > MAX_FILE_SIZE) {
                formik.setFieldError('logo', 'File should be less than 500Kb');
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.addEventListener('load', () => {
                const source = (reader.result as string) || '';
                const base64 = source.replace(
                    /^data:image\/(?:png|jpg|jpeg);base64,/,
                    ''
                );
                const img = new Image();
                img.src = source;
                img.addEventListener('load', () => {
                    if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
                        formik.setFieldError(
                            'logo',
                            `Image should not be larger than ${MAX_WIDTH}x${MAX_HEIGHT}px`
                        );
                        return;
                    }

                    formik.setFieldValue('logo', base64);
                });
            });
        }
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <div className="mb-4 flex items-center text-md font-semibold">
                <Building2 className="mr-2" />
                Customer Organization
            </div>
            <Grid className="mb-10">
                <Grid.Col span={6}>
                    <Group>
                        <Avatar
                            color="blue"
                            radius="50%"
                            size="xl"
                            src={base64ToImageSource(formik.values.logo)}
                        >
                            A
                        </Avatar>

                        <div>
                            <FileButton
                                accept="image/png,image/jpeg"
                                onChange={handleImageDrop}
                            >
                                {(props) => (
                                    <Button
                                        {...props}
                                        color="gray"
                                        size="xs"
                                        variant="outline"
                                    >
                                        Upload logo
                                    </Button>
                                )}
                            </FileButton>
                            {formik.errors.logo ? (
                                <div className="text-xs text-red-6">
                                    {formik.errors.logo}
                                </div>
                            ) : undefined}
                        </div>
                    </Group>
                </Grid.Col>
                <Grid.Col span={6}>
                    <div className="grid grid-cols-[max-content__1fr] gap-y-2 gap-x-4 text-sm">
                        <div>Customer Idn:</div>
                        <div>
                            {profile.idn}{' '}
                            <CopyButton
                                className="relative -bottom-[1px] leading-none"
                                value={profile.idn}
                            />
                        </div>
                    </div>
                </Grid.Col>

                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.organization_name &&
                            formik.values.organization_name
                                ? formik.errors.organization_name
                                : undefined
                        }
                        label="Organization name"
                        name="organization_name"
                        placeholder="Organization name"
                        value={formik.values.organization_name}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.website && formik.values.website
                                ? formik.errors.website
                                : undefined
                        }
                        label="Website"
                        name="website"
                        placeholder="https://example.com"
                        value={formik.values.website}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Select
                        data={[
                            { label: '1 - 49', value: 'below50' },
                            { label: '50 - 499', value: 'from50to500' },
                            { label: '500 - 4999', value: 'from500to5000' },
                            { label: '5000 +', value: 'over5000' }
                        ]}
                        label="Number of employees"
                        placeholder="Number of employees"
                        value={formik.values.number_of_employees}
                        onBlur={formik.handleBlur}
                        onChange={(count: string | null) => {
                            if (count) {
                                formik.setFieldValue(
                                    'number_of_employees',
                                    count
                                );
                            }
                        }}
                    />
                </Grid.Col>
                <Grid.Col>
                    <MultiSelect
                        data={INDUSTRIES}
                        label="Industry"
                        placeholder="Industry"
                        value={formik.values.industry}
                        onChange={(value) => {
                            formik.setFieldValue('industry', value);
                        }}
                    />
                </Grid.Col>
                <Grid.Col>
                    <Textarea
                        error={
                            formik.touched.self_description &&
                            formik.values.self_description
                                ? formik.errors.self_description
                                : undefined
                        }
                        label="Description"
                        name="self_description"
                        placeholder="Description"
                        rows={4}
                        value={formik.values.self_description}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>

                <Grid.Col>
                    <TextInput
                        error={
                            formik.touched.address?.address_line_1 &&
                            formik.values.address.address_line_1
                                ? formik.errors.address?.address_line_1
                                : undefined
                        }
                        label="Address"
                        name="address.address_line_1"
                        placeholder="Address"
                        value={formik.values.address.address_line_1}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.country && formik.values.country
                                ? formik.errors.country
                                : undefined
                        }
                        label="Country"
                        name="country"
                        placeholder="Country"
                        value={formik.values.country}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>

                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.address?.city &&
                            formik.values.address.city
                                ? formik.errors.address?.city
                                : undefined
                        }
                        label="City"
                        name="address.city"
                        placeholder="City"
                        value={formik.values.address.city}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.address?.state &&
                            formik.values.address.state
                                ? formik.errors.address?.state
                                : undefined
                        }
                        label="State"
                        name="address.state"
                        placeholder="State"
                        value={formik.values.address.state}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.address?.zipcode &&
                            formik.values.address.zipcode
                                ? formik.errors.address?.zipcode
                                : undefined
                        }
                        label="ZIP code"
                        name="address.zipcode"
                        placeholder="ZIP code"
                        value={formik.values.address.zipcode}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
            </Grid>

            <div className="mb-4 flex items-center text-md font-semibold">
                <User className="mr-2" />
                Personal Information
            </div>
            <Grid>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.first_name &&
                            formik.values.first_name
                                ? formik.errors.first_name
                                : undefined
                        }
                        label="First name"
                        name="first_name"
                        placeholder="First name"
                        value={formik.values.first_name}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.last_name && formik.values.last_name
                                ? formik.errors.last_name
                                : undefined
                        }
                        label="Last name"
                        name="last_name"
                        placeholder="Last name"
                        value={formik.values.last_name}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        error={
                            formik.touched.job_title && formik.values.job_title
                                ? formik.errors.job_title
                                : undefined
                        }
                        label="Job title"
                        name="job_title"
                        placeholder="Job title"
                        value={formik.values.job_title}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Input.Wrapper
                        error={
                            formik.touched.phone_number &&
                            formik.values.phone_number
                                ? formik.errors.phone_number
                                : undefined
                        }
                        label="Phone number"
                    >
                        <Input
                            component={IMaskInput}
                            mask="+00000000000"
                            name="phone_number"
                            placeholder="+00000000000"
                            value={formik.values.phone_number}
                            onChange={formik.handleChange}
                        />
                    </Input.Wrapper>
                </Grid.Col>
            </Grid>

            <div className="flex justify-end gap-2">
                <Button
                    className="w-52"
                    disabled={
                        isEqual(initialValues, formik.values) ||
                        !formik.isValid ||
                        formik.isSubmitting
                    }
                    loaderProps={{ type: 'dots' }}
                    loading={isPending}
                    size="md"
                    type="submit"
                >
                    Save
                </Button>
            </div>
        </form>
    );
};

export default AccountForm;
